import type { MasteredSite } from "../types";
import type { TrialInfo } from "@/views/Portfolio/types";

type SurveysTabProps = {
  site: MasteredSite;
  trials: TrialInfo[];
};

export default function SurveysTab({ site }: SurveysTabProps) {
  const hasProfile = !!(
    site.alchemer_id ||
    site.institution_name ||
    site.site_type ||
    site.site_affiliation ||
    site.org_network_name ||
    site.phone
  );

  if (!hasProfile) {
    return (
      <div className="flex flex-col items-center justify-center h-48 gap-2 text-[#8C8C8C]">
        <span className="text-[13px]">No site survey data available.</span>
      </div>
    );
  }

  const address = [
    site.profile_address,
    site.city,
    site.profile_state,
    site.profile_postal_code,
    site.country,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="rounded-[8px] border border-[#F0F2F4] bg-white shadow-[0_8px_24px_rgba(15,23,42,0.08)]">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#F0F2F4]">
          <div className="flex items-center gap-2">
            <span className="text-[13px] font-semibold text-[#1F2933]">
              Site Profile
            </span>
            {site.alchemer_id && (
              <span className="text-[11px] text-[#6B8FA8] bg-[#EEF4F8] rounded px-1.5 py-0.5">
                Alchemer survey
              </span>
            )}
          </div>
          {site.alchemer_id && (
            <span className="text-[11px] text-[#9CA3AF]">
              ID: {site.alchemer_id}
            </span>
          )}
        </div>

        <div className="grid grid-cols-2 gap-x-8 gap-y-3 px-4 py-4">
          {site.institution_name && (
            <ProfileField label="Institution" value={site.institution_name} />
          )}
          {site.dept_site_name && (
            <ProfileField
              label="Department / Site Name"
              value={site.dept_site_name}
            />
          )}
          {site.site_type && (
            <ProfileField label="Site Type" value={site.site_type} />
          )}
          {site.site_affiliation && (
            <ProfileField label="Affiliation" value={site.site_affiliation} />
          )}
          {site.org_network_name && (
            <ProfileField label="Network" value={site.org_network_name} />
          )}
          {site.eu_oms_registered != null && (
            <ProfileField
              label="EU OMS Registered"
              value={site.eu_oms_registered}
            />
          )}
          {site.phone && <ProfileField label="Phone" value={site.phone} />}
          {site.fax && <ProfileField label="Fax" value={site.fax} />}
          {address && <ProfileField label="Address" value={address} wide />}
        </div>
      </div>
    </div>
  );
}

function ProfileField({
  label,
  value,
  wide = false,
}: {
  label: string;
  value: string;
  wide?: boolean;
}) {
  return (
    <div className={wide ? "col-span-2" : ""}>
      <div className="text-[10px] font-medium text-[#8C8C8C] uppercase tracking-wide mb-0.5">
        {label}
      </div>
      <div className="text-[13px] text-[#1F2933]">{value}</div>
    </div>
  );
}
